import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import { Search, Square, Circle, Triangle, Star, Minus, Hexagon, Pentagon, Heart, Diamond, ArrowRight, X, Octagon, Plus, Database, RectangleHorizontal, Loader2, Lock } from 'lucide-react';
import { parseSvgForEditor, svgCanvasSize, DEFAULT_STROKE, DEFAULT_GRADIENT } from './svgUtils';
import { FRAME_SHAPES } from './frameShapes';
import MyFramesLibrary from './MyFramesLibrary';
import UpgradeModal from '../shared/UpgradeModal';
import IconThumbnail from '../shared/IconThumbnail';
import { isGenericName } from '../../utils/displayName';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const USER_KEY = 'ivisora_user';

const getUserPlan = () => {
  try {
    const u = JSON.parse(localStorage.getItem(USER_KEY) || 'null');
    return u?.plan || 'free';
  } catch { return 'free'; }
};

// Basic shapes
const basicShapes = [
  { id: 'rect', name: 'Rectangle', icon: Square, shape: 'rect' },
  { id: 'circle', name: 'Circle', icon: Circle, shape: 'circle' },
  { id: 'triangle', name: 'Triangle', icon: Triangle, shape: 'triangle' },
  { id: 'star', name: 'Star', icon: Star, shape: 'star' },
  { id: 'line', name: 'Line', icon: Minus, shape: 'line' },
  { id: 'roundedRect', name: 'Rounded', icon: Square, shape: 'roundedRect' },
  { id: 'hexagon', name: 'Hexagon', icon: Hexagon, shape: 'hexagon' },
  { id: 'pentagon', name: 'Pentagon', icon: Pentagon, shape: 'pentagon' },
  { id: 'octagon', name: 'Octagon', icon: Octagon, shape: 'octagon' },
  { id: 'heart', name: 'Heart', icon: Heart, shape: 'heart' },
  { id: 'diamond', name: 'Diamond', icon: Diamond, shape: 'diamond' },
  { id: 'arrow', name: 'Arrow', icon: ArrowRight, shape: 'arrow' },
  { id: 'cross', name: 'Cross', icon: Plus, shape: 'cross' },
  { id: 'cylinder', name: 'Cylinder', icon: Database, shape: 'cylinder' },
  { id: 'parallelogram', name: 'Parallelogram', icon: RectangleHorizontal, shape: 'parallelogram' },
];


// Re-add 50 sample icons
const sampleIcons = [
  '📱', '💼', '🛒', '✈️', '🎵', '⚽', '🌿', '☀️', '🏥', '💰', '🎮', '✉️', '📸', '🎨', '🏠', '🔒', '🛠️', '⏰', '🎁', '🚗', '⚡', '💻', '⌚', '⌨️', '🖱️', '🖨️', '📺', '📻', '📷', '📹', '🔍', '🔦', '🏮', '📔', '📕', '📖', '📗', '📘', '📙', '📚', '📓', '📒', '📃', '✏️', '✒️', '🖋️', '🖊️', '🖌️', '🖍️', '📝'
].map((emoji, i) => ({ id: `icon-emoji-${i}`, emoji, name: 'Emoji' }));

// Re-add 70 sample stickers
const sampleStickers = [
  '🎉', '❤️', '⭐', '🔥', '✨', '👍', '🎯', '💡', '🚀', '🏆', '💬', '📍', '💯', '👋', '👀', '🙌', '💥', '🌈', '💎', '🎈', '👑', '🍀', '🦄', '✌️', '💪', '🧠', '😎', '🤯', '🥳', '🤩', '🤑', '🤠', '😈', '👽', '👻', '🤖', '🎃', '🐶', '🐱', '🦊', '🐻', '🐼', '🦁', '🐯', '🐮', '🐷', '🐸', '🐵', '🐔', '🐧', '🎓', '🎸', '🎲', '🧩', '🧸', '🔮', '🧿', '💣', '🧨', '🍉', '🍓', '🥝', '🥑', '🍔', '🍟', '🍕', '🌭', '🌮', '🌯', '🍩', '🍪', '🍻', '🥂'
].map((emoji, i) => ({ id: `sticker-emoji-${i}`, emoji, name: 'Emoji' }));

// Each entry: { svg, name, defaultColor }
const memphisShapes = [
  {
    name: 'Blob',
    defaultColor: '#ff6b6b',
    svg: `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"><path fill="{fill}" d="M39.9,-65.4C55.2,-61.1,73.4,-57,83.1,-45.5C92.9,-34,94,-15,89.5,2.1C85,19.3,74.7,34.6,63.1,47C51.5,59.3,38.6,68.7,23.3,73.6C8.1,78.4,-9.4,78.7,-25.1,73C-40.8,67.3,-54.6,55.5,-64.1,41.4C-73.6,27.3,-78.7,10.9,-75.8,-4.2C-72.9,-19.3,-61.8,-33,-50.2,-44.7C-38.5,-56.3,-26.2,-65.9,-12.3,-70.6C1.6,-75.3,15.6,-75,24.6,-69.7Z" transform="translate(100 100)"/></svg>`,
  },
  {
    name: 'Blob 2',
    defaultColor: '#4ecdc4',
    svg: `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"><path fill="{fill}" d="M49,-76.3C61.4,-67.6,68.1,-49.7,76.5,-32.4C84.9,-15.1,95.2,1.6,92.5,16.5C89.9,31.5,74.5,44.7,60.8,55.4C47.1,66.1,35.1,74.3,20.8,79.5C6.4,84.7,-10.4,87,-26.6,83C-42.8,79.1,-58.4,68.9,-69.1,55.2C-79.8,41.4,-85.7,24,-88.2,6.5C-90.7,-11,-89.9,-28.5,-81.4,-42.6C-72.9,-56.6,-56.7,-67,-41.2,-73.9C-25.7,-80.7,-11,-84,4.2,-89.9C19.5,-95.8,36.5,-85.1,49,-76.3Z" transform="translate(100 100)"/></svg>`,
  },
  {
    name: 'Blob 3',
    defaultColor: '#aa96da',
    svg: `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"><path fill="{fill}" d="M54.2,-73.5C68.5,-63.2,77.3,-44.5,82.1,-25.2C86.9,-5.9,87.7,14,80.7,30.5C73.7,47,59,60.1,42.6,70.4C26.2,80.6,8.1,88.1,-8.4,86.5C-24.9,84.9,-39.8,74.2,-53.8,62C-67.7,49.8,-80.8,36.1,-85.3,20C-89.8,4,-85.7,-14.4,-77.1,-29.5C-68.5,-44.6,-55.5,-56.4,-41.2,-66.5C-26.9,-76.6,-11.3,-85,5.3,-91.7C21.8,-98.5,39.9,-83.7,54.2,-73.5Z" transform="translate(100 100)"/></svg>`,
  },
  {
    name: 'Dashed Circle',
    defaultColor: '#f38181',
    svg: `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"><circle cx="100" cy="100" r="80" fill="none" stroke="{fill}" stroke-width="15" stroke-dasharray="20 20"/></svg>`,
  },
  {
    name: 'Double Ring',
    defaultColor: '#ffe66d',
    svg: `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"><circle cx="100" cy="100" r="80" fill="none" stroke="{fill}" stroke-width="8"/><circle cx="100" cy="100" r="60" fill="none" stroke="{fill}" stroke-width="8"/></svg>`,
  },
  {
    name: 'Wave',
    defaultColor: '#55cbcd',
    svg: `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"><path fill="none" stroke="{fill}" stroke-width="18" stroke-linecap="round" d="M10 100 Q 50 30, 100 100 T 190 100"/></svg>`,
  },
  {
    name: 'Double Wave',
    defaultColor: '#f8b195',
    svg: `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"><path fill="none" stroke="{fill}" stroke-width="12" stroke-linecap="round" d="M10 80 Q 50 20, 100 80 T 190 80"/><path fill="none" stroke="{fill}" stroke-width="12" stroke-linecap="round" d="M10 120 Q 50 60, 100 120 T 190 120"/></svg>`,
  },
  {
    name: 'Zigzag',
    defaultColor: '#f67280',
    svg: `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"><polyline points="10,150 50,50 90,150 130,50 170,150 190,100" fill="none" stroke="{fill}" stroke-width="16" stroke-linejoin="round" stroke-linecap="round"/></svg>`,
  },
  {
    name: 'Diamond',
    defaultColor: '#95e1d3',
    svg: `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"><rect x="50" y="50" width="100" height="100" fill="none" stroke="{fill}" stroke-width="14" transform="rotate(45 100 100)"/></svg>`,
  },
  {
    name: 'Filled Diamond',
    defaultColor: '#c06c84',
    svg: `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"><polygon points="100,20 180,100 100,180 20,100" fill="{fill}"/></svg>`,
  },
  {
    name: 'Triangle',
    defaultColor: '#6c5b7b',
    svg: `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"><polygon points="100,20 180,175 20,175" fill="{fill}"/></svg>`,
  },
  {
    name: 'Triangle Outline',
    defaultColor: '#355c7d',
    svg: `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"><polygon points="100,20 180,175 20,175" fill="none" stroke="{fill}" stroke-width="14" stroke-linejoin="round"/></svg>`,
  },
  {
    name: 'Dot Grid',
    defaultColor: '#99b898',
    svg: `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"><circle cx="50" cy="50" r="16" fill="{fill}"/><circle cx="100" cy="50" r="16" fill="{fill}"/><circle cx="150" cy="50" r="16" fill="{fill}"/><circle cx="50" cy="100" r="16" fill="{fill}"/><circle cx="100" cy="100" r="16" fill="{fill}"/><circle cx="150" cy="100" r="16" fill="{fill}"/><circle cx="50" cy="150" r="16" fill="{fill}"/><circle cx="100" cy="150" r="16" fill="{fill}"/><circle cx="150" cy="150" r="16" fill="{fill}"/></svg>`,
  },
  {
    name: 'Four Dots',
    defaultColor: '#feceab',
    svg: `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"><circle cx="55" cy="55" r="32" fill="{fill}"/><circle cx="145" cy="55" r="32" fill="{fill}"/><circle cx="55" cy="145" r="32" fill="{fill}"/><circle cx="145" cy="145" r="32" fill="{fill}"/></svg>`,
  },
  {
    name: 'Cross',
    defaultColor: '#ff847c',
    svg: `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"><rect x="85" y="20" width="30" height="160" rx="8" fill="{fill}"/><rect x="20" y="85" width="160" height="30" rx="8" fill="{fill}"/></svg>`,
  },
  {
    name: 'Star',
    defaultColor: '#e84a5f',
    svg: `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"><polygon points="100,15 120,75 185,75 132,115 152,175 100,138 48,175 68,115 15,75 80,75" fill="{fill}"/></svg>`,
  },
  {
    name: 'Star Outline',
    defaultColor: '#a8e6cf',
    svg: `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"><polygon points="100,15 120,75 185,75 132,115 152,175 100,138 48,175 68,115 15,75 80,75" fill="none" stroke="{fill}" stroke-width="12" stroke-linejoin="round"/></svg>`,
  },
  {
    name: 'Spiral',
    defaultColor: '#dcedc1',
    svg: `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"><path d="M100,100 m-5,0 a5,5 0 1,1 10,0 a15,15 0 1,1 -30,0 a25,25 0 1,1 50,0 a35,35 0 1,1 -70,0 a45,45 0 1,1 90,0 a55,55 0 1,1 -110,0" fill="none" stroke="{fill}" stroke-width="10" stroke-linecap="round"/></svg>`,
  },
  {
    name: 'Lightning',
    defaultColor: '#ffd3b6',
    svg: `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"><polygon points="120,10 70,110 105,110 80,190 150,80 110,80" fill="{fill}"/></svg>`,
  },
  {
    name: 'Heart',
    defaultColor: '#ffaaa5',
    svg: `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"><path d="M100,160 C60,130 20,110 20,70 A40,40 0 0,1 100,55 A40,40 0 0,1 180,70 C180,110 140,130 100,160Z" fill="{fill}"/></svg>`,
  },
];

const memphisElements = memphisShapes.map((shape, i) => ({
  id: `memphis-base-${i}`,
  name: shape.name,
  color: shape.defaultColor,
  content: shape.svg.replace(/{fill}/g, '{color}'),
}));
const ElementsPanel = ({ onAddElement, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('shape');
  const [elementTypes, setElementTypes] = useState([
    { key: 'frames', name: 'Frames' },
    { key: 'shape', name: 'Basic Shapes' },
    { key: 'icon', name: 'Icons' },
    { key: 'sticker', name: 'Stickers' },
    { key: 'image', name: 'Memphis' },
  ]);
  const tabsContainerRef = useRef(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const userPlan = getUserPlan();

  const handlePremiumClick = (asset, addFn) => {
    if (asset.is_premium && userPlan === 'free') {
      setShowUpgradeModal(true);
      return;
    }
    addFn();
  };

  // Published admin assets fetched from backend, keyed by element-type key
  const [publishedAssets, setPublishedAssets] = useState({
    shape: [], icon: [], sticker: [], image: [],
  });
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [categoriesById, setCategoriesById] = useState({});

  useEffect(() => {
    if (!API_URL) return;
    setAssetsLoading(true);
    Promise.all([
      fetch(`${API_URL}/api/public/elements`).then(r => r.ok ? r.json() : []),
      fetch(`${API_URL}/api/public/categories?type=element`).then(r => r.ok ? r.json() : []),
      fetch(`${API_URL}/api/public/element-types`).then(r => r.ok ? r.json() : []),
    ])
      .then(([elements, categories, types]) => {
        let activeTypes = elementTypes;
        if (Array.isArray(types) && types.length > 0) {
          // Frames is a local, code-defined tab — always present regardless
          // of the backend element-type list.
          activeTypes = [{ key: 'frames', name: 'Frames' }, ...types.map(t => ({ key: t.key, name: t.name }))];
          setElementTypes(activeTypes);
        }

        const merged = {};
        activeTypes.forEach(t => { merged[t.key] = []; });
        (Array.isArray(elements) ? elements : []).forEach(el => {
          if (!merged[el.type]) merged[el.type] = [];
          merged[el.type].push(el);
        });
        setPublishedAssets(merged);

        const byId = {};
        (Array.isArray(categories) ? categories : []).forEach(c => { byId[c.id] = c; });
        setCategoriesById(byId);
      })
      .catch(() => {})
      .finally(() => setAssetsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll to selected tab
  useEffect(() => {
    if (tabsContainerRef.current) {
      const activeButton = tabsContainerRef.current.querySelector(`[data-testid="elements-tab-${activeTab}"]`);
      if (activeButton) {
        activeButton.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [activeTab]);

  const handleAddShape = (shapeType, shapeColor) => {
    let width = 150;
    let height = 150;

    if (shapeType === 'line') {
      width = 200;
      height = 4;
    } else if (shapeType === 'roundedRect' || shapeType === 'rect') {
      width = 200;
      height = 120;
    } else if (shapeType === 'arrow') {
      width = 150;
      height = 80;
    } else if (shapeType === 'parallelogram') {
      width = 180;
      height = 120;
    } else if (shapeType === 'cylinder') {
      width = 100;
      height = 140;
    } else if (shapeType === 'cross') {
      width = 120;
      height = 120;
    }

    onAddElement({
      type: 'shape',
      shape: shapeType,
      x: 100,
      y: 100,
      width,
      height,
      fill: shapeColor || '#6366f1',
      stroke: '#000000',
      strokeWidth: 0,
      cornerRadius: 0
    });
  };

  const handleAddFrame = (shapeId) => {
    onAddElement({
      type: 'frame',
      shape: shapeId,
      x: 100,
      y: 100,
      width: 300,
      height: 300,
      rotation: 0,
      fit: 'cover',
      image: null,
    });
  };

  // Insert a saved custom frame (from My Frames) onto the canvas.
  const handleAddCustomFrame = (def) => {
    onAddElement({
      type: 'frame',
      frameType: 'custom',
      frameSource: def.frameSource || 'custom',
      framePath: def.framePath,
      frameViewBox: def.frameViewBox || { w: 1, h: 1 },
      shape: 'custom',
      x: 100,
      y: 100,
      width: 300,
      height: 300,
      rotation: 0,
      fit: 'cover',
      image: null,
    });
  };

  const handleAddSticker = (sticker) => {
    // Add sticker as a special sticker element that scales properly
    onAddElement({
      type: 'sticker',
      emoji: sticker.emoji,
      x: 100,
      y: 100,
      width: 100,
      height: 100,
      fontSize: 64
    });
  };

  const handleAddMemphis = (element) => {
    onAddElement({
      type: 'svg',
      x: 100, y: 100,
      width: 150, height: 150,
      svgContent: element.content, // original, uses {color} template — never modify
      colors:   [],
      colorMap: {},
      fill:     element.color || '#6366f1',
      gradient: { ...DEFAULT_GRADIENT },
      stroke:   { ...DEFAULT_STROKE },
      rotation: 0,
      opacity:  1,
    });
  };

  // Add a published admin asset — fetches SVG inline if only a URL is available,
  // then extracts exact colors via DOMParser so the properties panel shows per-color slots.
  const handleAddPublishedAsset = async (asset, width = 150, height = 150) => {
    if (asset.svg_content) {
      // Memphis templates use {color} placeholder — route through Memphis handler
      if (asset.svg_content.includes('{color}')) {
        handleAddMemphis({ content: asset.svg_content, color: asset.color || '#6366f1' });
        return;
      }
      // Real SVG with actual colors — parse to extract original colors and build colorMap
      const { svgContent, colors, colorMap } = parseSvgForEditor(asset.svg_content);
      const { width: w, height: h } = svgCanvasSize(svgContent, Math.max(width, height));
      onAddElement({
        type: 'svg', x: 100, y: 100,
        width: w, height: h,
        svgContent,
        colors,
        colorMap,
        fill:     colors[0] || asset.color || '#000000',
        gradient: { ...DEFAULT_GRADIENT },
        stroke:   { ...DEFAULT_STROKE },
        rotation: 0, opacity: 1,
      });
      return;
    }
    const url = asset.image_url || asset.url;
    if (!url) return;
    if (/\.svg(\?.*)?$/i.test(url)) {
      try {
        const res = await fetch(url);
        if (res.ok) {
          const rawSvg = await res.text();
          const { svgContent, colors, colorMap } = parseSvgForEditor(rawSvg);
          const { width: w, height: h } = svgCanvasSize(svgContent, Math.max(width, height));
          onAddElement({
            type: 'svg', x: 100, y: 100,
            width: w, height: h,
            svgContent,                          // original, never modified
            colors,
            colorMap,
            fill:     colors[0] || asset.color || '#000000',
            gradient: { ...DEFAULT_GRADIENT },
            stroke:   { ...DEFAULT_STROKE },
            rotation: 0, opacity: 1,
          });
          return;
        }
      } catch (_) {}
    }
    onAddElement({ type: 'image', src: url, x: 100, y: 100, width, height });
  };

  // Group an array of published assets by category_id, sorted by category.order.
  // Returns an array of { categoryId, categoryName, items }.
  // Assets with missing/unknown category_id go into a final "Uncategorized" bucket.
  const groupByCategory = (assets) => {
    const buckets = new Map();
    const UNCAT = '__uncategorized__';
    assets.forEach(asset => {
      const cid = asset.category_id && categoriesById[asset.category_id] ? asset.category_id : UNCAT;
      if (!buckets.has(cid)) buckets.set(cid, []);
      buckets.get(cid).push(asset);
    });
    const sorted = [];
    Object.values(categoriesById)
      .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
      .forEach(cat => {
        if (buckets.has(cat.id)) {
          sorted.push({ categoryId: cat.id, categoryName: cat.name, items: buckets.get(cat.id) });
        }
      });
    if (buckets.has(UNCAT)) {
      sorted.push({ categoryId: UNCAT, categoryName: 'Uncategorized', items: buckets.get(UNCAT) });
    }
    return sorted;
  };

  const renderSvgThumb = (svgContent, alt) => (
    <IconThumbnail svg={svgContent} alt={alt} />
  );

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900">Elements</h3>
          {onClose && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search elements..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9"
            data-testid="elements-search-input"
          />
        </div>
      </div>

      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <div className="px-3 py-2 border-b flex-shrink-0">
          <div
            ref={tabsContainerRef}
            className="flex gap-2 overflow-x-auto pb-1"
            style={{
              scrollBehavior: 'smooth',
              scrollbarWidth: 'thin',
              scrollbarColor: '#d1d5db transparent'
            }}
          >
            {elementTypes.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveTab(cat.key)}
                className={`text-xs px-3 py-1.5 rounded-md whitespace-nowrap flex-shrink-0 transition-all border ${activeTab === cat.key
                  ? 'bg-purple-600 text-white border-purple-600'
                  : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-200'
                  }`}
                data-testid={`elements-tab-${cat.key}`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        <ScrollArea className="flex-1">
          {/* Loading indicator for published assets */}
          {assetsLoading && (
            <div className="flex items-center justify-center gap-2 py-2 text-xs text-gray-400">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading assets…
            </div>
          )}

          {activeTab === 'frames' && (
            <div className="p-4 space-y-6">
              <div className="mb-2">
                <h3 className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">Built-in Frames</h3>
                <p className="text-[11px] text-gray-400 mb-3">Drop a photo into a frame to fill it.</p>
                <div className="grid grid-cols-3 gap-3">
                  {FRAME_SHAPES.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => handleAddFrame(s.id)}
                      data-testid={`frame-${s.id}`}
                      className="aspect-square flex flex-col items-center justify-center gap-2 bg-gray-50 hover:bg-purple-50 border-2 border-transparent hover:border-purple-300 rounded-xl transition-all group"
                      title={s.name}
                    >
                      <svg viewBox="-0.05 -0.05 1.1 1.1" className="w-10 h-10 group-hover:scale-110 transition-transform">
                        <path d={s.path} fill="#c7d2fe" stroke="#6366f1" strokeWidth="0.025" />
                      </svg>
                      <span className="text-[10px] text-gray-600 font-medium overflow-hidden text-ellipsis whitespace-nowrap w-full text-center px-1">{s.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              <MyFramesLibrary onInsertFrame={handleAddCustomFrame} />
            </div>
          )}

          {activeTab === 'shape' && (
            <div className="p-4 space-y-6">
              <div className="mb-2">
                <h3 className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">Basic Shapes</h3>
                <div className="grid grid-cols-3 gap-3">
                  {basicShapes.map((shape) => {
                    const Icon = shape.icon;
                    return (
                      <button
                        key={shape.id}
                        onClick={() => handleAddShape(shape.shape, '#6366f1')}
                        className="aspect-square flex flex-col items-center justify-center gap-2 bg-gray-50 hover:bg-purple-50 border-2 border-transparent hover:border-purple-300 rounded-xl transition-all group"
                        data-testid={`shape-${shape.id}`}
                      >
                        <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-[10px] text-gray-600 font-medium capitalize overflow-hidden text-ellipsis whitespace-nowrap w-full text-center px-1">{shape.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {(publishedAssets.shape || []).length > 0 && groupByCategory(publishedAssets.shape || []).map(group => (
                <div key={group.categoryId} className="mb-2">
                  <h3 className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">{group.categoryName}</h3>
                  <div className="grid grid-cols-3 gap-3">
                    {group.items.map((asset) => (
                      <button
                        key={asset.id}
                        onClick={() => handlePremiumClick(asset, () => handleAddPublishedAsset(asset, asset.default_width || 150, asset.default_height || 150))}
                        className="relative aspect-square flex items-center justify-center bg-gray-50 hover:bg-purple-50 border-2 border-transparent hover:border-purple-300 rounded-xl transition-all group p-1 overflow-hidden"
                        title={asset.name}
                      >
                        {asset.svg_content
                          ? renderSvgThumb(asset.svg_content, asset.name)
                          : <img src={asset.url || asset.image_url} alt={asset.name} className="w-full h-full object-contain" />
                        }
                        {asset.is_premium && userPlan === 'free' && (
                          <div className="absolute inset-0 bg-black/20 rounded-xl flex items-center justify-center">
                            <Lock className="w-4 h-4 text-amber-500 drop-shadow" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'icon' && (
            <div className="p-4 space-y-6">
              <div className="mb-2">
                <h3 className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">Default</h3>
                <div className="grid grid-cols-5 gap-2">
                  {sampleIcons.map((icon) => (
                    <button
                      key={icon.id}
                      onClick={() => handleAddSticker({ emoji: icon.emoji })}
                      className="aspect-square flex flex-col items-center justify-center gap-1 bg-gray-50 hover:bg-purple-50 border-2 border-transparent hover:border-purple-300 rounded-xl transition-all"
                    >
                      <span className="text-2xl">{icon.emoji}</span>
                    </button>
                  ))}
                </div>
              </div>

              {(publishedAssets.icon || []).length > 0 && groupByCategory(publishedAssets.icon || []).map(group => (
                <div key={group.categoryId} className="mb-2">
                  <h3 className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">{group.categoryName}</h3>
                  <div className="grid grid-cols-5 gap-2">
                    {group.items.map((asset) => (
                      <button
                        key={asset.id}
                        onClick={() => handlePremiumClick(asset, () => handleAddPublishedAsset(asset, asset.default_width || 80, asset.default_height || 80))}
                        className="relative aspect-square flex items-center justify-center bg-gray-50 hover:bg-purple-50 border-2 border-transparent hover:border-purple-300 rounded-xl transition-all p-1 overflow-hidden"
                        title={asset.name}
                      >
                        {asset.svg_content
                          ? renderSvgThumb(asset.svg_content, asset.name)
                          : <img src={asset.url || asset.image_url} alt={asset.name} className="w-full h-full object-contain" />
                        }
                        {asset.is_premium && userPlan === 'free' && (
                          <div className="absolute inset-0 bg-black/20 rounded-xl flex items-center justify-center">
                            <Lock className="w-3 h-3 text-amber-500 drop-shadow" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'sticker' && (
            <div className="p-4 space-y-6">
              <div className="mb-2">
                <h3 className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">Default</h3>
                <div className="grid grid-cols-5 gap-2">
                  {sampleStickers.map((sticker) => (
                    <button
                      key={sticker.id}
                      onClick={() => handleAddSticker(sticker)}
                      className="aspect-square flex flex-col items-center justify-center bg-gray-50 hover:bg-purple-50 border-2 border-transparent hover:border-purple-300 rounded-xl transition-all group"
                    >
                      <span className="text-3xl group-hover:scale-125 transition-transform">{sticker.emoji}</span>
                    </button>
                  ))}
                </div>
              </div>

              {(publishedAssets.sticker || []).length > 0 && groupByCategory(publishedAssets.sticker || []).map(group => (
                <div key={group.categoryId} className="mb-2">
                  <h3 className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">{group.categoryName}</h3>
                  <div className="grid grid-cols-4 gap-2">
                    {group.items.map((asset) => (
                      <button
                        key={asset.id}
                        onClick={() => handlePremiumClick(asset, () => {
                          if (asset.emoji) handleAddSticker({ emoji: asset.emoji });
                          else handleAddPublishedAsset(asset, asset.default_width || 100, asset.default_height || 100);
                        })}
                        className="relative aspect-square flex items-center justify-center bg-gray-50 hover:bg-purple-50 border-2 border-transparent hover:border-purple-300 rounded-xl transition-all group p-1 overflow-hidden"
                        title={asset.name}
                      >
                        {asset.emoji
                          ? <span className="text-3xl group-hover:scale-125 transition-transform">{asset.emoji}</span>
                          : asset.svg_content
                            ? renderSvgThumb(asset.svg_content, asset.name)
                            : <img src={asset.image_url || asset.url} alt={asset.name} className="w-full h-full object-contain group-hover:scale-110 transition-transform" />
                        }
                        {asset.is_premium && userPlan === 'free' && (
                          <div className="absolute inset-0 bg-black/20 rounded-xl flex items-center justify-center">
                            <Lock className="w-4 h-4 text-amber-500 drop-shadow" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'image' && (
            <div className="p-4 space-y-6">
              <div className="mb-2">
                <h3 className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">Default</h3>
                <div className="grid grid-cols-3 gap-2">
                  {memphisElements.map((element) => (
                    <button
                      key={element.id}
                      onClick={() => handleAddMemphis(element)}
                      className="flex flex-col items-center gap-1 rounded-xl border-2 border-transparent hover:border-purple-300 transition-all group bg-gray-50 hover:bg-purple-50 p-2"
                      title={element.name}
                    >
                      <div className="w-full aspect-square flex items-center justify-center group-hover:scale-110 transition-transform">
                        <IconThumbnail svg={element.content.replace(/{color}/g, element.color)} alt={element.name} />
                      </div>
                      <span className="text-[9px] text-gray-500 font-medium truncate w-full text-center">{element.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {(publishedAssets.image || []).length > 0 && groupByCategory(publishedAssets.image || []).map(group => (
                <div key={group.categoryId} className="mb-2">
                  <h3 className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">{group.categoryName}</h3>
                  <div className="grid grid-cols-3 gap-2">
                    {group.items.map((asset) => (
                      <button
                        key={asset.id}
                        onClick={() => handlePremiumClick(asset, () => handleAddPublishedAsset(asset, asset.default_width || 150, asset.default_height || 150))}
                        className="relative flex flex-col items-center justify-center gap-1 rounded-xl border-2 border-transparent hover:border-purple-300 transition-all group bg-gray-50 hover:bg-purple-50 p-2 overflow-hidden"
                        title={asset.name}
                      >
                        {asset.svg_content
                          ? <div className="w-full aspect-square">{renderSvgThumb(asset.svg_content, asset.name)}</div>
                          : <img src={asset.url || asset.image_url} alt={asset.name} className="w-full aspect-square object-contain" />
                        }
                        {!isGenericName(asset.name) && (
                          <span className="text-[9px] text-gray-500 font-medium truncate w-full text-center">{asset.name}</span>
                        )}
                        {asset.is_premium && userPlan === 'free' && (
                          <div className="absolute inset-0 bg-black/20 rounded-xl flex items-center justify-center">
                            <Lock className="w-4 h-4 text-amber-500 drop-shadow" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>
      {showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} />}
    </div>
  );
};

export default ElementsPanel;
